import { CONSTANTS_TIME__client } from '@/constants/CONSTANTS_TIME__client';
import { CONSTANTS__client } from '@/constants/CONSTANTS__client';
import { use_effect } from '@/hooks/_base/use_effect';
import { use_slice_user } from '@/hooks/store/use_slices';
import { HOOK_GRID2D_FLOAT__update_possible_on_screen_cells_indices } from '@/pages/_base/grid2d/_hooks/HOOK_GRID2D_FLOAT__update_possible_on_screen_cells_indices';
import { HOOK_GRID2D__slug_offset } from '@/pages/_base/grid2d/_hooks/HOOK_GRID2D__slug_offset';
import { HOOK__get_div_size } from '@/pages/page_generation/_hooks/HOOK__get_div_size';
import { DTO__entity_with_grid2d } from '@shared/dto/+workspace/workspace_entity/_database_entity_with_grid2d_permissions/DTO__entity_with_grid2d';
import { DTO__grid2d } from '@shared/dto/grid2d/DTO__grid2d';
import { ENUM__grid2d_cell_size, ENUM_UTILS__grid2d_cell_size } from '@shared/dto/grid2d_cell/ENUM__grid2d_cell_size';
import { DTO__grid2d_virtual } from '@shared/dto/grid2d_virtual/DTO__grid2d_virtual';
import { throttle } from 'es-toolkit';
import { useMemo, useRef, useState } from 'react';
import { DRAG_AND_DROP__grid2d_external } from '../grid2d/DRAG_AND_DROP__grid2d_external';
import { DRAG_AND_DROP__grid2d_internal } from '../grid2d/DRAG_AND_DROP__grid2d_internal';
import { GRID2D_CELL__float } from '../grid2d_cell/views/GRID2D_CELL__float';
import { WATCHER_GRID2D_CHUNK__load_cells } from '../watchers/grid2d/WATCHER_GRID2D_CHUNK__load_cells';
import { WatcherImageToTextTranslate } from '../watchers/image_to_text/WatcherImageToTextTranslate';
import { ROW1D__grid2d_name } from './ROW1D__grid2d_name';
import { HOOK_ROW1D__float_virtualizer } from './_hooks/HOOK_ROW1D__float_virtualizer';

interface PROPS {
  grid2d: DTO__grid2d;
  class_name_grid2d?: string;
  entity: DTO__entity_with_grid2d;
}

export function ROW1D__float({ grid2d, entity, class_name_grid2d = 'overflow-auto' }: PROPS) {
  /*
   * STORE
   */

  const { user_settings } = use_slice_user(['user_settings']);
  const { ui_grid2d_cells_sort_newest_first } = user_settings;

  /*
   * STATE
   */
  const [is_hovered, set_is_hovered] = useState(false);

  const { ref_element__set: ref_row1d, div_element: div_row1d, size: div_row1d_size } = HOOK__get_div_size();

  const ref_first_image_index_on_page = useRef(0);

  const ref_is_slug_offset_locked_by_scroll = useRef(false);

  const ref_virtualizer_size = useRef(0);

  const max_side = useMemo(() => ENUM_UTILS__grid2d_cell_size.size_to_max_side(ENUM__grid2d_cell_size.XS), []);

  const ref_grid2d_virtual_previous = useRef<DTO__grid2d_virtual>(DTO__grid2d_virtual.CREATE_STUB());

  const grid2d_virtual_config = useMemo(() => {
    if (div_row1d_size.is_zero()) return { total_columns: 0, visible_cells_amount: 0 };
    if (grid2d.is_stub()) return { total_columns: 0, visible_cells_amount: 0 };

    let total_columns = get_total_columns();

    let visible_cells_amount = DTO__grid2d_virtual.get_visible_cells_amount(div_row1d_size.height, total_columns, max_side, grid2d);

    return { total_columns, visible_cells_amount };
  }, [div_row1d, div_row1d_size, max_side, grid2d, grid2d.total_cells_amount]);

  const { slug_offset, set_slug_offset, set_slug_offset_from_url } = HOOK_GRID2D__slug_offset(DTO__grid2d.CREATE_STUB(), ref_first_image_index_on_page);

  const grid2d_virtual: DTO__grid2d_virtual = useMemo(
    () => get_grid2d_virtual(),
    [slug_offset, grid2d, ui_grid2d_cells_sort_newest_first, grid2d_virtual_config],
  );

  const { virtualizer, try_recalculate_item_width } = HOOK_ROW1D__float_virtualizer(
    grid2d_virtual,
    grid2d,
    div_row1d,
    ui_grid2d_cells_sort_newest_first,
    max_side,
  );

  const grid2d_float_update_possible_on_screen_cells_indices = HOOK_GRID2D_FLOAT__update_possible_on_screen_cells_indices(
    div_row1d,
    grid2d_virtual,
    virtualizer,
    ref_first_image_index_on_page,
    slug_offset,
    ref_is_slug_offset_locked_by_scroll,
  );

  /*
   * EVENTS
   */

  /*
   * EFFECTS COMMON
   */

  use_effect(() => {
    const throttle_scroll_handler = throttle(
      grid2d_float_update_possible_on_screen_cells_indices,
      CONSTANTS_TIME__client.THROTTLE_GRID2D_FLOAT_UPDATE_POSSIBLE_ON_SCREEN_CELLS_INDICES,
    );

    div_row1d.addEventListener('scroll', throttle_scroll_handler);

    return () => div_row1d.removeEventListener('scroll', throttle_scroll_handler);
  }, [div_row1d, grid2d_float_update_possible_on_screen_cells_indices]);

  use_effect(() => {
    ref_virtualizer_size.current = virtualizer.getTotalSize();
  }, []);

  use_effect(() => {
    if (slug_offset === ref_first_image_index_on_page.current) return;

    ref_first_image_index_on_page.current = slug_offset;
  }, [slug_offset]);

  use_effect(
    () => grid2d_float_update_possible_on_screen_cells_indices(),
    [div_row1d_size, div_row1d, grid2d_virtual, ui_grid2d_cells_sort_newest_first, grid2d_float_update_possible_on_screen_cells_indices],
  );

  /*
   * EFFECTS
   */

  /*
   * METHODS
   */

  function get_grid2d_virtual() {
    if (grid2d_virtual_config.total_columns === 0) return DTO__grid2d_virtual.CREATE_STUB(); // grid2d_virtual and virtualizer properties are not calculated yet based on viewport size

    const out = new DTO__grid2d_virtual();

    out.grid2d = grid2d.clone();
    out.cells__amount__grid2d_total = grid2d.total_cells_amount;

    out.total_columns = grid2d_virtual_config.total_columns;
    out.cells__amount__visible = grid2d_virtual_config.visible_cells_amount;
    out.cell__to_load_index_absolute = slug_offset;
    out.ui_grid2d_cells_sort_newest_first = ui_grid2d_cells_sort_newest_first;
    out.virtualizer_size = ref_virtualizer_size.current;

    out.init(ref_grid2d_virtual_previous.current);

    ref_grid2d_virtual_previous.current = out;

    return out;
  }

  function get_total_columns() {
    let out = CONSTANTS__client.GRID2D_VIRTUAL_TOTAL_COLUMNS_DEFAULT;

    if (div_row1d_size.is_zero()) return out;

    out = Math.floor(div_row1d_size.width / (max_side + CONSTANTS__client.FLOAT_GRID_GAP));

    out = Math.max(out, 1);

    return out;
  }

  /*
   * RENDERS
   */
  function render_grid_cells() {
    try_recalculate_item_width();

    const virtual_items = virtualizer.getVirtualItems();

    const items = virtual_items.length > 0 ? virtual_items : [CONSTANTS__client.VIRTUAL_ITEM_STUB];

    return items.map(item => {
      const is_stub = item.index === CONSTANTS__client.VIRTUAL_ITEM_STUB_INDEX;
      const key_id = is_stub ? `${grid2d._id}-first-cell` : `${grid2d._id}-${item.index}`;

      const width = max_side;
      let height = is_stub ? max_side : item.size;

      return (
        <div
          id={key_id}
          key={key_id}
          style={{
            position: 'absolute',
            top: 0,
            //
            width: `${width + CONSTANTS__client.FLOAT_GRID_GAP}px`,
            height: `${height}px`,
            //
            transform: `translateX(${item.start}px)`,
          }}
        >
          <GRID2D_CELL__float
            use_offset_from_slug={false}
            index={item.index}
            can_show_buttons={false}
            {...{ width, height, grid2d, entity, grid2d_virtual, ui_grid2d_cells_sort_newest_first }}
          />
        </div>
      );
    });
  }

  /*
   * HTML
   */

  return (
    <>
      <WATCHER_GRID2D_CHUNK__load_cells {...{ entity, grid2d_virtual, grid2d }} />
      <WatcherImageToTextTranslate {...{ grid2d, entity }} />

      <div className="relative size-full" onMouseEnter={() => set_is_hovered(true)} onMouseLeave={() => set_is_hovered(false)}>
        <div ref={ref_row1d} className={`info_row1d relative w-full h-full bg-component overflow-x-auto ${class_name_grid2d}`}>
          <div
            className="info_row1d_cells h-full min-w-0"
            style={{
              position: 'relative',
              width: `${virtualizer.getTotalSize() + CONSTANTS__client.FLOAT_GRID_GAP * virtualizer.getVirtualItems().length}px`,
              // width: `calc(${grid2d_virtual.total_columns * (max_side + CONSTANTS__client.FLOAT_GRID_GAP)}px - 2px)`,
            }}
          >
            {render_grid_cells()}
          </div>
          <DRAG_AND_DROP__grid2d_external {...{ grid2d, entity }} />
          <DRAG_AND_DROP__grid2d_internal {...{ grid2d, entity }} />
        </div>
        {!is_hovered && <ROW1D__grid2d_name {...{ entity }} />}
      </div>
    </>
  );
}
